import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { RehubSupabaseTracking } from '@rehub/tracking/react';
import App from './App';
import { supabase } from './services/supabase';
import { isPublicTourWebsite } from './utils/publicTourSite';
import { applyWorldsBranding, isWorldsHost } from './utils/worldsHost';

// On the 360Worlds consumer hosts, use the 360Worlds favicon/title instead of
// the 360Tours Studio branding baked into index.html.
applyWorldsBranding();

class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; error: Error | null }
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('[ErrorBoundary] Caught:', error, info.componentStack);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 40, color: '#f87171', background: '#0f172a', minHeight: '100vh', fontFamily: 'monospace' }}>
                    <h1 style={{ fontSize: 24, marginBottom: 16 }}>Something went wrong</h1>
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#e2e8f0' }}>
                        {this.state.error?.message}
                    </pre>
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#94a3b8', marginTop: 12 }}>
                        {this.state.error?.stack}
                    </pre>
                    <button
                        onClick={() => window.location.reload()}
                        style={{ marginTop: 24, padding: '10px 20px', background: '#f97316', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
                    >
                        Reload Page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function ToursTracking() {
    const [path, setPath] = useState(() => window.location.pathname);

    useEffect(() => {
        const onNav = () => setPath(window.location.pathname);
        window.addEventListener('popstate', onNav);
        return () => window.removeEventListener('popstate', onNav);
    }, []);

    // 360Worlds tour hosts never load the ReHub cookie dialog. On 360 Tours,
    // the dialog stays on the studio and stays off the public tour website.
    if (isWorldsHost()) return null;

    return (
        <RehubSupabaseTracking
            app="tours"
            mode="events-only"
            supabase={supabase}
            showConsentBanner={!isPublicTourWebsite(path)}
        />
    );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <ErrorBoundary>
            <App />
            <ToursTracking />
        </ErrorBoundary>
    </React.StrictMode>
);
